import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, extractApiError } from '../api/client';
import { SkeletonText } from '../components/SkeletonLoader';
import type { Startup } from '@shared/types';
import { BarChart2, FlaskConical, MessageSquare, TrendingUp, Zap } from 'lucide-react';

// Polish 1: Archive confirmation modal
function ArchiveConfirmModal({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <>
      <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(2px)' }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: '#0d0f1a', border: '1px solid #2a2a3e', borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 400, zIndex: 1001,
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>
          Archive {name}?
        </h3>
        <p style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6, marginBottom: 20 }}>
          You can restore it later from your archived startups.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onConfirm} style={{
            flex: 1, background: '#ef4444', border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700, padding: '10px 0', cursor: 'pointer',
          }}>
            Archive
          </button>
          <button onClick={onCancel} style={{
            flex: 1, background: 'transparent', border: '1px solid #2a2a3e',
            borderRadius: 10, color: '#9ca3af', fontSize: 13, padding: '10px 0', cursor: 'pointer',
          }}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

const STAGE_OPTIONS = [
  { value: 'idea', label: 'Idea' },
  { value: 'validation', label: 'Validation' },
  { value: 'mvp', label: 'MVP' },
  { value: 'growth', label: 'Growth' },
];

const NAV_LINKS = [
  { href: 'financials', label: 'Financials', desc: 'Model your revenue scenarios' },
  { href: 'beta', label: 'Beta Users', desc: 'Manage beta testers' },
  { href: 'feedback', label: 'Feedback', desc: 'View collected feedback' },
  { href: 'dashboard', label: 'Dashboard', desc: 'Investor-ready metrics' },
];

export default function StartupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [startup, setStartup] = useState<Startup | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Startup>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchStartup = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get(`/startups/${id}`);
        setStartup(data.data.startup);
        setEditData(data.data.startup);
      } catch (err) {
        setError(extractApiError(err).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStartup();
  }, [id]);

  // Update browser tab title
  useEffect(() => {
    if (startup?.name) {
      document.title = `${startup.name} — StartupScore`;
    }
    return () => { document.title = 'StartupScore'; };
  }, [startup?.name]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { data } = await api.put(`/startups/${id}`, editData);
      setStartup(data.data.startup);
      setIsEditing(false);
    } catch (err) {
      alert(extractApiError(err).message);
    } finally {
      setIsSaving(false);
    }
  };

  // Polish 1: Archive with confirmation modal
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const handleArchive = async () => {
    await api.delete(`/startups/${id}`);
    navigate('/startups');
  };

  if (isLoading) return (
    <div className="max-w-3xl">
      <div className="skeleton h-8 w-48 mb-4" />
      <SkeletonText lines={3} />
    </div>
  );

  if (error) return (
    <div className="card text-center py-12">
      <p className="text-red-400 mb-4">{error}</p>
      <Link to="/startups" className="btn-secondary">← Back to startups</Link>
    </div>
  );

  if (!startup) return null;

  const isPlaceholderName = !startup.name || /^Idea from [A-Za-z]+ \d+$/.test(startup.name);

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/startups" className="hover:text-white transition-colors">My Startups</Link>
        <span>/</span>
        <span className="text-gray-300">{startup.name || 'Unnamed Startup'}</span>
      </div>

      {/* B8: Pipeline-origin banner */}
      {startup.fromPipelineId && (
        <div style={{
          background: 'rgba(0,212,255,0.08)',
          border: '1px solid rgba(0,212,255,0.25)',
          borderRadius: 12,
          padding: '12px 18px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          color: '#00d4ff',
        }}>
          <Zap size={16} />
          <span>This startup was created from your Pipeline blueprint — your idea, positioning, and MVP plan are pre-filled.</span>
        </div>
      )}

      {/* C: Rename prompt for placeholder names */}
      {isPlaceholderName && !isEditing && (
        <div style={{
          background: 'rgba(255,184,0,0.08)',
          border: '1px solid rgba(255,184,0,0.3)',
          borderRadius: 12,
          padding: '12px 18px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#ffb800' }}>
            <span></span>
            <span>Give your startup a real name — "{startup.name || 'Unnamed'}" is just a placeholder.</span>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            style={{
              background: 'rgba(255,184,0,0.15)',
              border: '1px solid rgba(255,184,0,0.4)',
              borderRadius: 8,
              color: '#ffb800',
              fontSize: 12,
              fontWeight: 700,
              padding: '5px 14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Rename →
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-xl sm:text-3xl font-bold text-white" style={{ wordBreak: 'break-word' }}>{startup.name}</h1>
            {/* Polish 4: Show targetSegment/category instead of stage badge (more useful context) */}
            {startup.targetSegment && (
              <span className="badge bg-brand-500/10 text-brand-500 border border-brand-500/30" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {startup.targetSegment}
              </span>
            )}
          </div>
          <p className="text-gray-400 text-sm">{startup.stage ? `Stage: ${STAGE_OPTIONS.find(s => s.value === startup.stage)?.label ?? startup.stage}` : ''}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="btn-secondary text-sm"
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
          <button onClick={() => setShowArchiveModal(true)} className="text-sm text-gray-500 hover:text-red-400 transition-colors px-3">
            Archive
          </button>
        </div>
      </div>

      {/* Quick navigation */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {NAV_LINKS.map((link) => (
          <Link
            key={link.href}
            to={`/startups/${id}/${link.href}`}
            className="card hover:border-brand-500/50 transition-all group"
          >
            <div className="font-medium text-white group-hover:text-brand-500 transition-colors mb-1">{link.label}</div>
            <div className="text-gray-500 text-xs">{link.desc}</div>
          </Link>
        ))}
      </div>

      {/* Detail card */}
      <div className="card space-y-6">
        {isEditing ? (
          <>
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={editData.name ?? startup.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="input-field"
                placeholder="e.g. InvoiceZap"
              />
            </div>

            <div>
              <label className="label">Stage</label>
              <select
                value={editData.stage || startup.stage}
                onChange={(e) => setEditData({ ...editData, stage: e.target.value as Startup['stage'] })}
                className="input-field"
              >
                {STAGE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Description</label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={3}
                className="input-field resize-none"
              />
            </div>

            <div>
              <label className="label">Problem Statement</label>
              <textarea
                value={editData.problemStatement || ''}
                onChange={(e) => setEditData({ ...editData, problemStatement: e.target.value })}
                rows={3}
                className="input-field resize-none"
              />
            </div>

            <div>
              <label className="label">Unique Value Proposition</label>
              <textarea
                value={editData.uniqueValueProposition || ''}
                onChange={(e) => setEditData({ ...editData, uniqueValueProposition: e.target.value })}
                rows={2}
                className="input-field resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button onClick={handleSave} disabled={isSaving} className="btn-primary">
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => setIsEditing(false)} className="btn-secondary">Cancel</button>
            </div>
          </>
        ) : (
          <>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
              <p className="text-gray-200">{startup.description}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Problem Statement</h3>
              <p className="text-gray-200">{startup.problemStatement}</p>
            </div>

            {startup.uniqueValueProposition && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Unique Value Proposition</h3>
                <p className="text-gray-200">{startup.uniqueValueProposition}</p>
              </div>
            )}

            {startup.competitors && startup.competitors.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Competitive Landscape</h3>
                <div className="space-y-2">
                  {startup.competitors.filter((c) => c.name).map((c, i) => (
                    <div key={i} className="flex items-start gap-3 bg-[#0f0f1a] rounded-lg p-3">
                      <span className="font-medium text-white text-sm w-28 shrink-0">{c.name}</span>
                      <span className="text-gray-400 text-sm">Weakness: {c.weakness}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {startup.skills && startup.skills.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Team Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {startup.skills.map((s) => (
                    <span key={s} className="badge bg-brand-500/10 text-brand-500 border border-brand-500/30">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Polish 1: Archive confirmation modal */}
      {showArchiveModal && (
        <ArchiveConfirmModal
          name={startup.name || 'this startup'}
          onConfirm={() => { setShowArchiveModal(false); handleArchive(); }}
          onCancel={() => setShowArchiveModal(false)}
        />
      )}
    </div>
  );
}
